import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Cat, Product, CartItem } from '../types';

interface CartState {
  items: CartItem[];
  addItem: (item: Cat | Product, type: 'cat' | 'product') => boolean;
  removeItem: (id: string, type: 'cat' | 'product') => void;
  updateQuantity: (id: string, type: 'cat' | 'product', quantity: number) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  getItem: (id: string, type: 'cat' | 'product') => CartItem | undefined;
}

const generateId = (item: Cat | Product, type: 'cat' | 'product') => `${type}-${item._id}`;

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item, type) => {
        if (type === 'cat' && (item as Cat).listingType !== 'sale') return false;
        const id = generateId(item, type);
        const existingItem = get().items.find((i) => i.id === id);
        if (existingItem) return false;

        set((state) => ({
          items: [...state.items, { id, type, item, quantity: 1 }],
        }));
        return true;
      },

      removeItem: (id, type) => {
        const itemId = generateId({ _id: id } as Cat | Product, type);
        set((state) => ({
          items: state.items.filter((i) => i.id !== itemId),
        }));
      },

      updateQuantity: (id, type, quantity) => {
        const itemId = generateId({ _id: id } as Cat | Product, type);
        if (quantity <= 0) {
          get().removeItem(id, type);
          return;
        }
        set((state) => ({
          items: state.items.map((i) =>
            i.id === itemId ? { ...i, quantity } : i
          ),
        }));
      },

      clearCart: () => set({ items: [] }),

      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },

      getTotalPrice: () => {
        return get().items.reduce((total, item) => total + item.item.price * item.quantity, 0);
      },

      getItem: (id, type) => {
        const itemId = generateId({ _id: id } as Cat | Product, type);
        return get().items.find((i) => i.id === itemId);
      },
    }),
    {
      name: 'cat-care-cart',
      partialize: (state) => ({ items: state.items }),
    }
  )
);
